"use client";

import type { Table } from "@tanstack/react-table";
import { Download } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import jsonToCsvExport from "json-to-csv-export";

interface DataTableExportProps<TData> {
    table: Table<TData>;
    isLoading?: boolean;
    onExport?: () => void;
}

export function DataTableExport<TData>({
    table,
    isLoading,
    onExport: onExportOverride,
}: DataTableExportProps<TData>) {
    const { t } = useTranslation("common");
    const defaultOnExport = React.useCallback(() => {
        const visibleColumns = table
            .getVisibleLeafColumns()
            .filter((column) => column.getCanSort());

        const transformedData = table.getFilteredRowModel().rows.map((row) => {
            const rowData: Record<string, any> = {};
            visibleColumns.forEach((column) => {
                rowData[column.id] = row.getValue(column.id) || "";
            });
            return rowData;
        });

        jsonToCsvExport({
            data: transformedData,
            filename: `export-${new Date().toISOString().split('T')[0]}.csv`,
            headers: visibleColumns.map((column) => column.id),
        });
    }, [table]);

    const handleClick = onExportOverride ?? defaultOnExport;
    // When an override is provided the caller drives the dataset (e.g. a date-range
    // modal hitting the API), so the "no rows on screen" guard would be misleading.
    const disabled = isLoading || (!onExportOverride && table.getFilteredRowModel().rows.length === 0);

    return (
        <Button
            variant="outline"
            size="sm"
            className="h-8 px-2 lg:px-3"
            onClick={handleClick}
            disabled={disabled}
        >
            <Download className="h-4 w-4" />
            {t("dataTable.export")}
        </Button>
    );
}
